import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Trash2, ArrowRight, Repeat, Lightbulb, Download, FileSpreadsheet } from "lucide-react";
import { useBranches, useProducts, useStockTransfers, useCreateStockTransfer } from "@/api/inventory";
import { useTransferSuggestions, TransferSuggestionItem, downloadReportCsv } from "@/api/reports";
import { getApiErrorMessage } from "@/api/client";

function ExportButtons({ endpoint, filename }: { endpoint: string; filename: string }) {
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const handleExport = async (format: "csv" | "xlsx") => {
    setExporting(format);
    try {
      await downloadReportCsv(`${endpoint}.${format}`, `${filename}.${format}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setExporting(null);
    }
  };
  return (
    <div className="ml-auto flex shrink-0 gap-2">
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("xlsx")}>
        <FileSpreadsheet size={13} className="mr-1" /> Excel
      </button>
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("csv")}>
        <Download size={13} className="mr-1" /> CSV
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Trasladar Productos (multi-sucursal): traslado directo e instantáneo entre
 * dos sucursales — no hay estado "en tránsito" ni que aprobar; al confirmar,
 * el backend descuenta el stock de la sucursal origen y lo suma a la destino
 * en una sola transacción (o rechaza todo el traslado si falta stock en
 * alguna línea). El historial de abajo es el registro inmutable de cada
 * traslado ya aplicado.
 */
export default function StockTransfersPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { data: transfers, isLoading } = useStockTransfers();
  const { data: suggestions, isLoading: isLoadingSuggestions, refetch: refetchSuggestions } = useTransferSuggestions();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Trasladar Productos</h1>
          <p className="text-sm text-gray-500">Mueve inventario de una sucursal a otra al instante. El traslado se aplica de una sola vez, sin estados intermedios.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nuevo traslado
        </button>
      </div>

      {!isLoadingSuggestions && suggestions && suggestions.length > 0 && (
        <TransferSuggestionsPanel suggestions={suggestions} onApplied={() => refetchSuggestions()} />
      )}

      <div className="card overflow-hidden">
        <div className="flex items-center gap-1.5 border-b border-gray-100 px-4 py-3">
          <Repeat size={15} className="text-gray-400" />
          <p className="text-sm font-semibold text-gray-900">Histórico de traslados</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Fecha</th>
              <th className="px-4 py-2.5">Origen</th>
              <th className="px-4 py-2.5" />
              <th className="px-4 py-2.5">Destino</th>
              <th className="px-4 py-2.5">Productos</th>
              <th className="px-4 py-2.5">Realizado por</th>
              <th className="px-4 py-2.5">Notas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {transfers?.map((t) => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-600">{formatDate(t.createdAt)}</td>
                <td className="px-4 py-2.5 text-gray-900">{t.sourceBranch?.name}</td>
                <td className="px-4 py-2.5 text-gray-300">
                  <ArrowRight size={14} />
                </td>
                <td className="px-4 py-2.5 text-gray-900">{t.destinationBranch?.name}</td>
                <td className="px-4 py-2.5 text-gray-600">
                  {t.lines.map((l) => `${l.product?.name ?? "?"} (${l.quantity})`).join(", ")}
                </td>
                <td className="px-4 py-2.5 text-gray-600">{t.createdBy?.fullName ?? "—"}</td>
                <td className="px-4 py-2.5 text-gray-500">{t.notes ?? "—"}</td>
              </tr>
            ))}
            {transfers?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Todavía no se han registrado traslados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateTransferModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

interface SuggestionGroup {
  key: string;
  fromBranchId: string;
  fromBranchName: string;
  toBranchId: string;
  toBranchName: string;
  items: TransferSuggestionItem[];
}

/**
 * Agrupa las sugerencias (una fila por producto) por par sucursal
 * origen/destino, porque un traslado real solo tiene UNA sucursal origen y
 * UNA destino con varias líneas — no una por producto. Cada grupo se
 * aplica con un clic reutilizando `useCreateStockTransfer`, el mismo hook
 * del formulario manual de abajo (mismo endpoint, mismo comportamiento
 * instantáneo, mismo rechazo si falta stock).
 */
function TransferSuggestionsPanel({ suggestions, onApplied }: { suggestions: TransferSuggestionItem[]; onApplied: () => void }) {
  const groups = useMemo(() => {
    const map = new Map<string, SuggestionGroup>();
    for (const s of suggestions) {
      const key = `${s.fromBranchId}:${s.toBranchId}`;
      const existing = map.get(key);
      if (existing) existing.items.push(s);
      else map.set(key, { key, fromBranchId: s.fromBranchId, fromBranchName: s.fromBranchName, toBranchId: s.toBranchId, toBranchName: s.toBranchName, items: [s] });
    }
    return Array.from(map.values());
  }, [suggestions]);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-gray-100 px-4 py-3">
        <Lightbulb size={15} className="text-amber-500" />
        <p className="text-sm font-semibold text-gray-900">Traslados sugeridos</p>
        <span className="text-xs text-gray-400">— productos con excedente en una sucursal y faltante en otra, según su stock mínimo</span>
        <ExportButtons endpoint="/reports/store-transfer-suggestions" filename="sugerencias-traslado" />
      </div>
      <div className="divide-y divide-gray-100">
        {groups.map((g) => (
          <SuggestionGroupRow key={g.key} group={g} onApplied={onApplied} />
        ))}
      </div>
    </div>
  );
}

function SuggestionGroupRow({ group, onApplied }: { group: SuggestionGroup; onApplied: () => void }) {
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(group.items.map((i) => [i.productId, String(i.suggestedQty)]))
  );
  const createTransfer = useCreateStockTransfer();

  const handleApply = async () => {
    const lines = group.items.map((i) => ({ productId: i.productId, quantity: quantities[i.productId] ?? "0" })).filter((l) => Number(l.quantity) > 0);
    if (lines.length === 0) {
      toast.error("Ajusta al menos una cantidad mayor a cero.");
      return;
    }
    try {
      await createTransfer.mutateAsync({ sourceBranchId: group.fromBranchId, destinationBranchId: group.toBranchId, notes: "Generado desde traslados sugeridos", lines });
      toast.success(`Traslado de ${group.fromBranchName} a ${group.toBranchName} aplicado.`);
      onApplied();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
          {group.fromBranchName} <ArrowRight size={13} className="text-gray-400" /> {group.toBranchName}
        </p>
        <button type="button" className="btn-primary" disabled={createTransfer.isPending} onClick={handleApply}>
          {createTransfer.isPending ? "Aplicando..." : "Crear este traslado"}
        </button>
      </div>
      <div className="space-y-1.5">
        {group.items.map((item) => (
          <div key={item.productId} className="grid grid-cols-12 items-center gap-2 text-sm">
            <span className="col-span-6 truncate text-gray-700">
              {item.productName} <span className="text-xs text-gray-400">({item.sku})</span>
            </span>
            <span className="col-span-3 text-xs text-gray-500">
              {item.fromBranchStock} disp. → {item.toBranchStock} en destino (mín. {item.minStock})
            </span>
            <input
              className="input-field col-span-3"
              type="number"
              min="0"
              value={quantities[item.productId] ?? ""}
              onChange={(e) => setQuantities((q) => ({ ...q, [item.productId]: e.target.value }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface DraftLine {
  productId: string;
  quantity: string;
}

function CreateTransferModal({ onClose }: { onClose: () => void }) {
  const { data: branches } = useBranches(true);
  const { data: productsResponse } = useProducts({ pageSize: 200, isActive: true });
  const products = productsResponse?.data ?? [];
  const createTransfer = useCreateStockTransfer();

  const [sourceBranchId, setSourceBranchId] = useState("");
  const [destinationBranchId, setDestinationBranchId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ productId: "", quantity: "1" }]);

  useEffect(() => {
    if (branches && branches.length > 0 && !sourceBranchId) {
      setSourceBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, sourceBranchId]);

  const addLine = () => setLines((prev) => [...prev, { productId: "", quantity: "1" }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const destinationOptions = (branches ?? []).filter((b) => b.id !== sourceBranchId);
  const isValid =
    sourceBranchId && destinationBranchId && sourceBranchId !== destinationBranchId && lines.every((l) => l.productId && Number(l.quantity) > 0);

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Elige sucursal de origen, destino (distinto al origen) y completa cada línea con producto y cantidad.");
      return;
    }
    try {
      await createTransfer.mutateAsync({
        sourceBranchId,
        destinationBranchId,
        notes: notes || undefined,
        lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
      });
      toast.success("Traslado aplicado — el inventario ya se actualizó en ambas sucursales.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nuevo traslado de productos</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal origen</label>
              <select className="input-field mt-1" value={sourceBranchId} onChange={(e) => setSourceBranchId(e.target.value)}>
                <option value="">Seleccione...</option>
                {branches?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal destino</label>
              <select className="input-field mt-1" value={destinationBranchId} onChange={(e) => setDestinationBranchId(e.target.value)}>
                <option value="">Seleccione...</option>
                {destinationOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <input className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Motivo del traslado, referencia, etc." />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Productos a trasladar</p>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={addLine}>
                + Agregar línea
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <select className="input-field col-span-8" value={line.productId} onChange={(e) => updateLine(index, { productId: e.target.value })}>
                    <option value="">Producto...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Cantidad"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                  <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeLine(index)} aria-label="Quitar línea">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-400">
              La disponibilidad de stock en la sucursal origen se valida al confirmar; si algún producto no tiene suficiente, el traslado completo se rechaza.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createTransfer.isPending} onClick={handleSubmit}>
            {createTransfer.isPending ? "Trasladando..." : "Confirmar traslado"}
          </button>
        </div>
      </div>
    </div>
  );
}
